// src/lib/facture-snapshot.js — ouverture d'un document facture archivé.
//
// ⚠️ POURQUOI CE FICHIER EXISTE.
// `openFactureSnapshot` était dupliqué à l'identique dans src/App.jsx et
// src/tabs/HistoriquePanel.jsx. Au chantier « numéro attribué à l'envoi », la
// copie d'HistoriquePanel a reçu la substitution des jetons de brouillon, pas
// celle d'App.jsx : le bouton « Voir la facture » de l'onglet Facturation
// affichait un document contenant littéralement `N° __INVOICE_NUMBER__` et
// `Émise le __INVOICE_DATE__` — imprimable et exportable en PDF, sur le chemin
// commission. Deux copies = une à oublier. Il n'y en a plus qu'une.
//
// Toute évolution du RENDU d'un snapshot archivé se fait ICI, nulle part ailleurs.

import { inflateFactureSnapshot, renderDraftPlaceholders } from './facture-html'
import { fetchInvoiceSnapshot } from './invoices'
import { ouvrirApercuIsole } from './apercu-isole'

/**
 * Prépare un html_snapshot archivé pour un rendu visuel (aperçu, impression, PDF).
 *
 * ORDRE IMPOSÉ, ne pas intervertir :
 *   1. renderDraftPlaceholders — jetons de brouillon (`__INVOICE_NUMBER__`,
 *      `__INVOICE_DATE__`) → libellés lisibles. No-op strict sur une pièce émise
 *      (aucun jeton), donc applicable SANS condition : pas de test de type à
 *      maintenir, pas de branche à oublier.
 *   2. inflateFactureSnapshot — placeholder logo → data URI réelle.
 *
 * ⚠️ Fonction d'AFFICHAGE. Le résultat ne doit JAMAIS être réécrit en base : le
 * snapshot archivé garde ses jetons jusqu'à l'attribution du numéro, qui est la
 * seule opération autorisée à les substituer (RPC assign_invoice_number, en SQL).
 *
 * @param {string} raw - html_snapshot brut (table invoices ou factLog KV)
 * @returns {string} HTML prêt à écrire dans une fenêtre ; '' si rien à afficher
 */
export function renderSnapshotForDisplay(raw) {
  return inflateFactureSnapshot(renderDraftPlaceholders(raw || ''))
}

/**
 * Ouvre l'aperçu d'une facture dans un nouvel onglet (chantier Pass A, Point 5).
 * Source 1 = factLog KV `htmlSnapshot` ; repli = table invoices (`html_snapshot`)
 * quand l'entrée ne porte qu'un `invoiceId`. Lecture seule, aucune écriture.
 *
 * Les dépendances sont injectables pour permettre un test sans DOM ni réseau —
 * c'est ce qui rend vérifiable le fait que les jetons ne fuient jamais à l'écran.
 *
 * @param {object} factEntry - { htmlSnapshot?, invoiceId? }
 * @param {object} [deps]
 * @param {function} [deps.fetchSnapshot] - (invoiceId) => Promise<string|null>
 * @param {function} [deps.openWindow]    - () => Window|null
 * @param {function} [deps.onError]       - (message) => void
 */
export async function openFactureSnapshot(factEntry, deps = {}) {
  const {
    fetchSnapshot = fetchInvoiceSnapshot,
    openWindow = () => window.open('', '_blank'),
    onError = (m) => window.alert(m),
  } = deps

  let raw = factEntry && factEntry.htmlSnapshot
  if (!raw && factEntry && factEntry.invoiceId) {
    raw = await fetchSnapshot(factEntry.invoiceId)
  }
  const html = renderSnapshotForDisplay(raw)
  if (!html) {
    onError('Snapshot indisponible pour cette facture (ancienne entrée non migrée).')
    return
  }
  // Faille N1 (lot L5) : la copie archivée (facture, avoir, reçu) s'ouvre dans
  // un cadre isolé sans scripts, jamais écrite dans une page de l'appli. Elle
  // est montrée telle quelle, jamais réécrite.
  ouvrirApercuIsole(html, { openWindow })
}

/**
 * Charge un snapshot archivé pour l'ENVOYER (corps d'un e-mail), pas pour
 * l'ouvrir : même chemin de rendu que l'aperçu (renderSnapshotForDisplay,
 * ordre jetons → logo), sans fenêtre. Chantier « L'avoir part chez le
 * propriétaire » : l'e-mail de l'avoir joint le document archivé de l'avoir —
 * jamais un document regénéré côté client, qui pourrait diverger du numéro
 * tiré en base. Lecture seule, aucune écriture.
 *
 * @param {string} invoiceId
 * @param {object} [deps]
 * @param {function} [deps.fetchSnapshot] - (invoiceId) => Promise<string|null>
 * @returns {Promise<string|null>} HTML prêt à joindre ; null si rien en base
 */
export async function loadSnapshotForEmail(invoiceId, deps = {}) {
  const { fetchSnapshot = fetchInvoiceSnapshot } = deps
  if (!invoiceId) return null
  const raw = await fetchSnapshot(invoiceId)
  const html = renderSnapshotForDisplay(raw)
  return html || null
}
